// Mission-visits API actions. Types live in `./missionVisits.types`,
// shared helpers in `./missionVisits.helpers`. This file only contains
// the HTTP action wrappers — each is a single call into `serviceFetch`
// guarded by `withWrappedError` so ServiceHttpError surfaces as a
// friendly Error with a domain prefix.

import { serviceFetch, ServiceHttpError } from '../http/serviceLayerClient'
import { buildCtx, withWrappedError } from './missionVisits.helpers'
import {
  ACTIVE_MISSION_EXISTS_CODE,
  ActiveMissionExistsError,
} from './missionVisits.types'
import type {
  AnchorEvent,
  ComplianceStatus,
  LeadRejectionReason,
  OptimizeRouteResult,
  RejectLeadRequest,
  SkipReason,
  VisitLeadRequest,
} from './missionVisits.types'

// ── Lead actions ─────────────────────────────────────────────────────

export async function visitLead(
  missionId: string,
  leadId: string,
  data?: VisitLeadRequest,
): Promise<void> {
  await withWrappedError('Failed to record visit', async () => {
    const ctx = await buildCtx()
    await serviceFetch(`/v1/missions/${missionId}/leads/${leadId}/visit`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
      ctx,
    })
  })
}

export async function skipLead(
  missionId: string,
  leadId: string,
  reason: SkipReason,
): Promise<void> {
  await withWrappedError('Failed to skip lead', async () => {
    const ctx = await buildCtx()
    await serviceFetch(`/v1/missions/${missionId}/leads/${leadId}/skip`, {
      method: 'POST',
      body: JSON.stringify({ state_reason: reason }),
      ctx,
    })
  })
}

// Reject a lead with one of the spec reasons. `snoozeUntil` is required
// when the reason is 'visit_later' but enforced at the call site.
export async function rejectLead(
  missionId: string,
  leadId: string,
  reason: LeadRejectionReason,
  snoozeUntil?: string,
): Promise<void> {
  await withWrappedError('Failed to reject lead', async () => {
    const ctx = await buildCtx()
    const body: RejectLeadRequest = { rejection_reason: reason }
    if (snoozeUntil) body.snooze_until = snoozeUntil
    await serviceFetch(`/v1/missions/${missionId}/leads/${leadId}/reject`, {
      method: 'POST',
      body: JSON.stringify(body),
      ctx,
    })
  })
}

export async function rejectMission(missionId: string, reason?: string): Promise<void> {
  await withWrappedError('Failed to reject mission', async () => {
    const ctx = await buildCtx()
    // Wire key is `rejection_reason` (MissionRejectRequest) — REQUIRED, no
    // default. Was sent as `reason` (not a field on that schema at all), which
    // omitted the required key entirely; every call would have 422'd.
    await serviceFetch(`/v1/missions/${missionId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ rejection_reason: reason ?? '' }),
      ctx,
    })
  })
}

// Visit-report SUBMIT wrappers live in the cohesive sibling module
// `./missionVisitReports` (they share the idempotency + envelope-unwrap idiom).
// Re-exported here so the public import path (`@appic/lib/api/missionVisits`)
// is unchanged for every existing consumer.
export {
  submitVisitReport,
  submitVisitReportV2,
  submitVisitReportV8,
} from './missionVisitReports'

export async function getComplianceStatus(userId: string): Promise<ComplianceStatus> {
  return withWrappedError('Failed to check compliance', async () => {
    const ctx = await buildCtx()
    const { data } = await serviceFetch<{ success: boolean; data: ComplianceStatus }>(
      `/v1/missions/compliance/${userId}`,
      { ctx },
    )
    // Service layer sometimes nests the payload under `.data` and
    // sometimes returns it at the top level — accept both.
    return ((data as unknown as { data?: ComplianceStatus }).data ?? data) as ComplianceStatus
  })
}

// ── Mission lifecycle (T60) ──────────────────────────────────────────

// Start (open) a mission day. The SL enforces a single-active-mission
// invariant: if the rep already has an active mission, it answers
// `409 ACTIVE_MISSION_EXISTS` with `details.blocking_mission_id`. We
// translate that into a typed `ActiveMissionExistsError` so the caller
// can resume the already-active mission instead of failing silently
// (BUG-FE-TAGESPLANUNG-OPEN-SESSION-DIRECT-SUPABASE-RLS-AND-409-2026-06-07).
export async function startMissionDay(missionId: string): Promise<void> {
  const ctx = await buildCtx()
  try {
    await serviceFetch(`/v1/missions/${missionId}/start`, {
      method: 'POST',
      body: JSON.stringify({}),
      ctx,
    })
  } catch (error) {
    if (
      error instanceof ServiceHttpError &&
      error.status === 409 &&
      error.code === ACTIVE_MISSION_EXISTS_CODE
    ) {
      const blockingRaw = error.details?.blocking_mission_id
      const blockingMissionId =
        typeof blockingRaw === 'string' && blockingRaw ? blockingRaw : null
      throw new ActiveMissionExistsError(blockingMissionId, error.message)
    }
    throw error
  }
}

export async function finishMissionDay(missionId: string): Promise<void> {
  const ctx = await buildCtx()
  await serviceFetch(`/v1/missions/${missionId}/finish`, {
    method: 'POST',
    body: JSON.stringify({}),
    ctx,
  })
}

export async function recordHomeReturn(missionId: string): Promise<void> {
  const ctx = await buildCtx()
  await serviceFetch(`/v1/missions/${missionId}/home-return`, {
    method: 'POST',
    body: JSON.stringify({}),
    ctx,
  })
}

// ── Route optimization ───────────────────────────────────────────────

// Trigger Google's route optimisation for a mission. Reorders
// `mission_leads.order_index` to match the optimal sequence. Use only
// on explicit user action — it consumes a quota slot at Google.
export async function optimizeRoute(
  missionId: string,
  trafficAware = false,
): Promise<OptimizeRouteResult> {
  const ctx = await buildCtx()
  const response = await serviceFetch(`/v1/routes/optimize/${missionId}`, {
    method: 'POST',
    body: JSON.stringify({ traffic_aware: trafficAware }),
    ctx,
  })
  const envelope = response.data as Record<string, unknown>
  return (envelope?.data ?? envelope) as OptimizeRouteResult
}

// Read pre-computed cached variants (primary, reversed, traffic-aware)
// without triggering a fresh Google call. Use this on page load.
//
// Returns null when the cache lookup fails — the consumer treats that
// as "no cached variants yet", but we log the underlying error so a
// broken endpoint or auth issue isn't masked silently (D-33).
export async function getRouteVariants(
  missionId: string,
): Promise<OptimizeRouteResult | null> {
  const ctx = await buildCtx()
  try {
    const response = await serviceFetch(`/v1/routes/${missionId}/variants`, {
      method: 'GET',
      ctx,
    })
    const envelope = response.data as Record<string, unknown>
    return (envelope?.data ?? envelope) as OptimizeRouteResult
  } catch (err) {
    // Never-dark (P3 "stale data poses as live"): a 404 is a genuine "no cached
    // variant yet" → null (valid "no route"). ANY other failure (5xx /
    // transport) is a READ FAILURE that must surface loudly — swallowing it to
    // null would pose as "no route" indistinguishably from a real empty result,
    // letting the morning-sync write an empty route cache over good data.
    if (err instanceof ServiceHttpError && err.status === 404) return null
    throw err
  }
}

// ── Anchor events (T03/T04) ──────────────────────────────────────────

const ANCHOR_EVENT_TYPE: Record<'promote' | 'demote', 'promoted' | 'demoted'> = {
  promote: 'promoted',
  demote: 'demoted',
}

export async function postAnchorEvent(
  stopId: string,
  action: 'promote' | 'demote',
  reason?: string,
): Promise<void> {
  const ctx = await buildCtx()
  // Wire key is `event_type` (AnchorEventRequest), matching the regex
  // `^(promoted|demoted)$` — REQUIRED. Was sent as `action: 'promote'|'demote'`
  // (wrong key AND wrong tense on the value); every call would have 422'd.
  // Public signature unchanged; only the outgoing wire body is corrected.
  await serviceFetch(`/v1/missions/stops/${stopId}/anchor-event`, {
    method: 'POST',
    body: JSON.stringify({ event_type: ANCHOR_EVENT_TYPE[action], reason: reason || null }),
    ctx,
  })
}

export async function getAnchorEvents(
  missionId: string,
  leadId: string,
): Promise<AnchorEvent[]> {
  const ctx = await buildCtx()
  const { data } = await serviceFetch<AnchorEvent[]>(
    `/v1/missions/${missionId}/leads/${leadId}/anchor-events`,
    { ctx },
  )
  return data
}

export async function updateMissionStop(
  missionId: string,
  stopId: string,
  update: { is_anchor?: boolean; preferred_time?: string | null },
): Promise<void> {
  const ctx = await buildCtx()
  await serviceFetch(`/v1/missions/${missionId}/stops/${stopId}`, {
    method: 'PATCH',
    body: JSON.stringify(update),
    ctx,
  })
}
